import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Card, CardHeader } from 'Components/_theme'
import { ShippingAddressFormSchema } from 'Components/ShippingAddressForm/ShippingAddressForm.types'

import { iconLostDevice } from 'Icons/iconLostDevice'

import { ReplacementType } from 'Portal/__generated__/globalTypes'

import { LostOrStolenModalConfirmation } from './components/LostOrStolenModalConfirmation/LostOrStolenModalConfirmation'
import { LostOrStolenModalLanding } from './components/LostOrStolenModalLanding/LostOrStolenModalLanding'
import { LostOrStolenModalReplacementForm } from './components/LostOrStolenModalReplacementForm/LostOrStolenModalReplacementForm'
import { LostOrStolenModalShippingForm } from './components/LostOrStolenModalShippingForm/LostOrStolenModalShippingForm'
import { LostOrStolenModalSummary } from './components/LostOrStolenModalSummary/LostOrStolenModalSummary'
import {
  LostOrStolenModalProps,
  LostOrStolenModalStepsEnum,
  LostOrStolenModalStepsMap,
  LostOrStolenModalTypesEnum,
  LostStolenReplacementFormSchema,
} from './LostOrStolenModal.types'
import { lostStolenReplacementFormDefaultValues } from './LostOrStolenModal.values'

export const LostOrStolenModal = (props: LostOrStolenModalProps) => {
  const { rental, type, onClose, onSubmit, setOnAfterClose } = props
  const [selectionType, setSelectionType] = useState(ReplacementType.none)
  const [replacementFormData, setReplacementFormData] = useState(
    lostStolenReplacementFormDefaultValues,
  )
  const [shippingFormData, setShippingFormData] =
    useState<ShippingAddressFormSchema | null>()
  const [currentStep, setCurrentStep] =
    useState<`${LostOrStolenModalStepsEnum}`>(
      LostOrStolenModalStepsEnum.LANDING,
    )
  const requestIdRef = useRef<string | null>(null)
  const messageRef = useRef<string | null>(null)

  const handleExitModal = useCallback(() => {
    setCurrentStep(LostOrStolenModalStepsEnum.LANDING)
    // reset forms state
    setReplacementFormData(lostStolenReplacementFormDefaultValues)
    setShippingFormData(null)
    requestIdRef.current = null
    messageRef.current = null
  }, [])

  const handleSetCurrentStep = useCallback(
    (
      step: `${LostOrStolenModalStepsEnum}`,
      shippingData?: ShippingAddressFormSchema,
      replacementData?: LostStolenReplacementFormSchema,
    ) => {
      if (shippingData) {
        setShippingFormData({ ...shippingData })
      }

      if (replacementData) {
        setReplacementFormData({ ...replacementData })
      }

      setCurrentStep(step)
    },
    [],
  )

  const stepsMap = useMemo((): LostOrStolenModalStepsMap => {
    switch (type) {
      case LostOrStolenModalTypesEnum.ORGANISATION_WITH_DEPOT:
        return {
          [LostOrStolenModalStepsEnum.LANDING]: {
            component: LostOrStolenModalLanding,
            props: {
              isNoReplacementEnabled: rental.isLostStolenNoReplacementEnabled,
              isReplacementDeviceAvailableInDepot:
                rental.isReplacementDeviceAvailableInDepot,
              onSetCurrentStep: handleSetCurrentStep,
              onSetSelectionType: setSelectionType,
              onSetShippingFormData: setShippingFormData,
              type,
            },
          },
          [LostOrStolenModalStepsEnum.REPLACEMENT_FORM]: {
            component: LostOrStolenModalReplacementForm,
            props: {
              onSetCurrentStep: handleSetCurrentStep,
              rental,
            },
          },
          [LostOrStolenModalStepsEnum.SHIPPING_FORM]: {
            component: LostOrStolenModalShippingForm,
            props: {
              onSetCurrentStep: handleSetCurrentStep,
              rental,
              shippingFormData,
            },
          },
          [LostOrStolenModalStepsEnum.SUMMARY]: {
            component: LostOrStolenModalSummary,
            props: {
              messageRef,
              onSetCurrentStep: handleSetCurrentStep,
              onSubmit,
              rental,
              replacementFormData,
              requestIdRef,
              selectionType,
              shippingFormData,
            },
          },
          [LostOrStolenModalStepsEnum.CONFIRMATION]: {
            component: LostOrStolenModalConfirmation,
            props: {
              messageRef,
              onClose,
              requestIdRef,
              selectionType,
              shippingFormData,
            },
          },
        }
      default:
        return {
          [LostOrStolenModalStepsEnum.LANDING]: {
            component: LostOrStolenModalLanding,
            props: {
              isNoReplacementEnabled: rental.isLostStolenNoReplacementEnabled,
              onSetCurrentStep: handleSetCurrentStep,
              onSetSelectionType: setSelectionType,
              onSetShippingFormData: setShippingFormData,
              type,
            },
          },
          [LostOrStolenModalStepsEnum.SHIPPING_FORM]: {
            component: LostOrStolenModalShippingForm,
            props: {
              onSetCurrentStep: handleSetCurrentStep,
              rental,
              selectionType,
              shippingFormData,
            },
          },
          [LostOrStolenModalStepsEnum.SUMMARY]: {
            component: LostOrStolenModalSummary,
            props: {
              messageRef,
              onSetCurrentStep: handleSetCurrentStep,
              onSubmit,
              rental,
              replacementFormData,
              requestIdRef,
              selectionType,
              shippingFormData,
            },
          },
          [LostOrStolenModalStepsEnum.CONFIRMATION]: {
            component: LostOrStolenModalConfirmation,
            props: {
              messageRef,
              onClose,
              requestIdRef,
              selectionType,
              shippingFormData,
            },
          },
        }
    }
  }, [
    handleSetCurrentStep,
    onClose,
    onSubmit,
    rental,
    replacementFormData,
    selectionType,
    shippingFormData,
    type,
  ])

  const renderStep = useMemo(() => {
    const { component: Component, props } = stepsMap[currentStep]

    return { component: <Component {...props} /> }
  }, [stepsMap, currentStep])

  useEffect(() => {
    setOnAfterClose(handleExitModal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleExitModal])

  return (
    <Card>
      <CardHeader icon={iconLostDevice}>
        <FormattedMessage id="LostOrStolenModal_header" />
      </CardHeader>

      {renderStep.component}
    </Card>
  )
}
